import {TargetType} from '@/enum';
import {validateName, validateNumber, ValidationError} from '@/validate';
import {isCloudStorage} from '@/storage/cloud';

type FormValues = {
  target: TargetType;
  endpoint: string;
  name: string;
  item: string;
  url: string;
  accessKey: string;
  secretKey: string;
  arn: string;
  externalid: string;
  credentials: string;
  importer: boolean;
  useWith: string[];
  storageClass: string;
  cautionThreshold: any;
  warningThreshold: any;
  optionalData: any;
  writeLimit: any;
  account: string;
  secret: string;
  quota: string;
  units: string;
}

const maxNumber = 100;
const maxNumberOptional = 85;

export const validateStorageTarget = ({target}: FormValues) => {
  const errors = {} as any;
  if (!target) {
    errors.target = ValidationError.CANNOT_BE_BLANK;
  }
  return errors;
};

export const validateAuthorization = (
  {target, accessKey, secretKey, arn, externalid, url, credentials, account, secret}: FormValues,
) => {
  const errors = {} as any;

  if (isCloudStorage(target)) {
    if (target === TargetType.S3OTHER) {
      if (!accessKey) {
        errors.accessKey = ValidationError.CANNOT_BE_BLANK;
      }
      if (!secretKey) {
        errors.secretKey = ValidationError.CANNOT_BE_BLANK;
      }
    }
    if (target === TargetType.AZURE) {
      if (!account) {
        errors.account = ValidationError.CANNOT_BE_BLANK;
      }
      if (!secret) {
        errors.secret = ValidationError.CANNOT_BE_BLANK;
      }
    }
    if (target === TargetType.S3) {
      if (!arn && externalid) {
        errors.externalid = 'External ID requires a Role ARN';
      }
    } else if (target === TargetType.S3OTHER) {
      if (!url) {
        errors.url = ValidationError.CANNOT_BE_BLANK;
      }
    } else if (target === TargetType.GOOGLE) {
      if (!credentials) {
        errors.credentials = ValidationError.CANNOT_BE_BLANK;
      }
    }
  }

  return errors;
};

export const validateEditAuthorization = (
  {target, accessKey, secretKey, arn, externalid, credentials, secret}: FormValues,
) => {
  const errors = {} as any;

  if (isCloudStorage(target)) {
    if (target === TargetType.S3OTHER) {
      if (!accessKey) {
        errors.accessKey = ValidationError.CANNOT_BE_BLANK;
      }
      if (!secretKey) {
        errors.secretKey = ValidationError.CANNOT_BE_BLANK;
      }
    }
    if (target === TargetType.AZURE) {
      if (!secret) {
        errors.secret = ValidationError.CANNOT_BE_BLANK;
      }
    }
    if (target === TargetType.S3) {
      if (!arn && externalid) {
        errors.externalid = 'External ID requires a Role ARN';
      }
    } else if (target === TargetType.GOOGLE) {
      if (!credentials) {
        errors.credentials = ValidationError.CANNOT_BE_BLANK;
      }
    }
  }

  return errors;
};

export const validateParameters = (
  {target, name, item, storageClass, useWith,
    cautionThreshold, warningThreshold, optionalData, writeLimit, quota, units}: FormValues) => {
  const errors = {} as any;

  if (!item) {
    errors.item = ValidationError.CANNOT_BE_BLANK;
  }

  if (!storageClass) {
    errors.storageClass = ValidationError.CANNOT_BE_BLANK;
  }

  if (target === TargetType.BLACKPEARL || target === TargetType.BPPOLICY || target === TargetType.VOLUME ||
      target === TargetType.BPVOLUME || target === TargetType.BLOCKVOLUME) {
    if (cautionThreshold || cautionThreshold === 0) {
      errors.cautionThreshold = validateNumber(cautionThreshold, {maxValue: maxNumber});
    }
    if (warningThreshold || warningThreshold === 0) {
      errors.warningThreshold = validateNumber(warningThreshold, {maxValue: maxNumber});
    }
    if (cautionThreshold && warningThreshold && !errors.cautionThreshold && !errors.warningThreshold &&
      cautionThreshold >= warningThreshold) {
      errors.cautionThreshold = 'Caution Threshold cannot be equal/above Warning Threshold';
    }
  }

  if (target === TargetType.BPLINK && (!useWith || useWith.length != 1)) {
    errors.useWith = 'A single bucket must be selected';
  }

  if ((target === TargetType.VOLUME || target === TargetType.BLOCKVOLUME || target === TargetType.BPVOLUME) &&
    (optionalData || optionalData === 0)) {
    errors.optionalData = validateNumber(optionalData, {maxValue: maxNumberOptional});
  }

  if (writeLimit) {
    if (isNaN(Number(writeLimit))) {
      errors.writeLimit = 'Write Limit must be a number';
    } else {
      if (!cautionThreshold || cautionThreshold === 0) {
        errors.writeLimit = 'Write Limit cannot be set without a Caution Threshold';
      }
    }
  }
  if (quota) {
    if (isNaN(Number(quota))) {
      errors.quota = 'Quota must be a number';
    } else {
      if (Number(quota) === 0) {
        errors.quota = 'Quota cannot be 0.  Leave field blank for no quota';
      }
      if (Number(quota) < 0) {
        errors.quota = 'Quota must be positive';
      }
    }
    if (units === null || units === undefined || units === '') {
      errors.units = 'Units cannot be blank if quota specified';
    }
  }

  errors.name = validateName(name);

  return errors;
};

export const validateEditParameters = (
  {target, name, cautionThreshold, warningThreshold, optionalData, writeLimit, quota, units}: FormValues) => {
  const errors = {} as any;

  if (target === TargetType.BLACKPEARL || target === TargetType.BPPOLICY || target === TargetType.VOLUME ||
    target === TargetType.BPVOLUME || target === TargetType.BLOCKVOLUME) {
    if (cautionThreshold || cautionThreshold === 0) {
      errors.cautionThreshold = validateNumber(cautionThreshold, {maxValue: maxNumber});
    }
    if (warningThreshold || warningThreshold === 0) {
      errors.warningThreshold = validateNumber(warningThreshold, {maxValue: maxNumber});
    }
    if (cautionThreshold && warningThreshold && !errors.cautionThreshold && !errors.warningThreshold &&
      cautionThreshold >= warningThreshold) {
      errors.cautionThreshold = 'Caution Threshold cannot be equal/above Warning Threshold';
    }
  }

  if ((target === TargetType.VOLUME || target === TargetType.BLOCKVOLUME || target === TargetType.BPVOLUME) &&
    (optionalData || optionalData === 0)) {
    errors.optionalData = validateNumber(optionalData, {maxValue: maxNumberOptional});
  }

  if (writeLimit) {
    if (isNaN(Number(writeLimit))) {
      errors.writeLimit = 'Write Limit must be a number';
    } else {
      if (!cautionThreshold || cautionThreshold === 0) {
        errors.writeLimit = 'Write Limit cannot be set without a Caution Threshold';
      }
    }
  }
  if (quota) {
    if (isNaN(Number(quota))) {
      errors.quota = 'Quota must be a number';
    } else {
      if (Number(quota) === 0) {
        errors.quota = 'Quota cannot be 0.  Leave field blank to clear quota';
      }
      if (Number(quota) < 0) {
        errors.quota = 'Quota must be positive';
      }
    }
    if (units === null || units === undefined || units === '') {
      errors.units = 'Units cannot be blank if quota specified';
    }
  }

  errors.name = validateName(name);

  return errors;
};
